class AnniversaryLetterSection extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.loading = this.querySelector('[data-loading]');
    this.progress = this.querySelector('[data-progress]');
    this.progressBar = this.querySelector('[data-progress-bar]');
    this.openButton = this.querySelector('[data-open-letter]');
    this.coverStage = this.querySelector('[data-cover-stage]');
    this.letter = this.querySelector('[data-letter]');
    this.videoStage = this.querySelector('[data-video-stage]');
    this.mediaQuery = window.matchMedia('(max-width: 767px)');
    this.timers = [];
    this.isPageScrollLocked = false;
    this.lockPageScroll();

    this.openButton?.addEventListener('click', () => this.openLetter(), { once: true });
    this.prepare();
  }

  disconnectedCallback() {
    this.timers?.forEach((timer) => window.clearTimeout(timer));
    this.unlockPageScroll();
  }

  lockPageScroll() {
    if (this.isPageScrollLocked) return;
    this.previousBodyOverflow = document.body.style.overflow;
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
    window.scrollTo(0, 0);
    document.body.style.overflow = 'hidden';
    this.isPageScrollLocked = true;
  }

  unlockPageScroll() {
    if (!this.isPageScrollLocked) return;
    document.body.style.overflow = this.previousBodyOverflow || '';
    this.isPageScrollLocked = false;
  }

  async prepare() {
    const minimumDelay = new Promise((resolve) => {
      this.timers.push(window.setTimeout(resolve, 1100));
    });

    const images = this.getPreloadImages();
    const video = this.getActiveVideo();
    let loadedImages = 0;
    const imageTasks = images.map((image) => this.waitForImage(image).finally(() => {
      loadedImages += 1;
      this.setProgress(images.length ? (loadedImages / images.length) * 100 : 100);
    }));

    if (!images.length) this.setProgress(100);
    await Promise.all([minimumDelay, ...imageTasks]);
    this.setProgress(100);
    this.finishLoading();
    if (video) this.waitForVideo(video);
  }

  getPreloadImages() {
    const isMobile = this.mediaQuery.matches;
    return [...this.querySelectorAll('.anniversary-letter__background-image, .anniversary-letter__cover-image')]
      .filter((image) => {
        if (isMobile && image.classList.contains('anniversary-letter__desktop-only')) return false;
        if (!isMobile && image.classList.contains('anniversary-letter__mobile-only')) return false;
        return true;
      });
  }

  waitForImage(image) {
    if (image.complete) return Promise.resolve();
    return new Promise((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        resolve();
      };
      image.addEventListener('load', finish, { once: true });
      image.addEventListener('error', finish, { once: true });
      this.timers.push(window.setTimeout(finish, 12000));
    });
  }

  waitForVideo(video) {
    const sources = [...video.querySelectorAll('source[data-src]')];
    sources.forEach((source) => {
      source.src = source.dataset.src;
      source.removeAttribute('data-src');
    });
    video.preload = 'auto';
    video.load();

    if (video.readyState >= HTMLMediaElement.HAVE_ENOUGH_DATA) return Promise.resolve();
    return new Promise((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        resolve();
      };
      video.addEventListener('canplaythrough', finish, { once: true });
      video.addEventListener('error', finish, { once: true });
      this.timers.push(window.setTimeout(finish, 15000));
    });
  }

  getActiveVideo() {
    const preferredMedia = this.mediaQuery.matches ? 'mobile' : 'desktop';
    const fallbackMedia = this.mediaQuery.matches ? 'desktop' : 'mobile';
    return this.querySelector(`[data-letter-video="${preferredMedia}"]`)
      || this.querySelector(`[data-letter-video="${fallbackMedia}"]`);
  }

  setProgress(value) {
    const progress = Math.max(0, Math.min(100, Math.round(value)));
    if (this.progressBar) this.progressBar.style.transform = `scaleX(${progress / 100})`;
    this.progress?.setAttribute('aria-valuenow', String(progress));
  }

  finishLoading() {
    this.classList.add('is-ready');
    this.loading?.classList.add('is-leaving');
    this.timers.push(window.setTimeout(() => this.loading?.remove(), 700));
  }

  openLetter() {
    const video = this.getActiveVideo();
    if (!video) {
      this.unlockPageScroll();
      this.classList.add('is-opening');
      this.showLetter();
      return;
    }

    this.videoStage.hidden = false;
    video.hidden = false;
    video.classList.add('is-active-video');
    this.classList.add('is-video-playing');

    const fadeCover = () => {
      if (this.classList.contains('is-opening')) return;
      this.classList.add('is-opening');
    };

    const onFirstFrame = () => {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(fadeCover);
      });
    };

    if (typeof video.requestVideoFrameCallback === 'function') {
      video.requestVideoFrameCallback(() => onFirstFrame());
    } else {
      video.addEventListener('playing', onFirstFrame, { once: true });
    }

    this.timers.push(window.setTimeout(fadeCover, 800));
    video.addEventListener('ended', () => this.showLetter(), { once: true });
    video.addEventListener('error', () => this.showLetter(), { once: true });

    const playRequest = video.play();
    playRequest?.catch(() => this.showLetter());
  }

  showLetter() {
    const activeVideo = this.getActiveVideo();
    activeVideo?.pause();
    this.unlockPageScroll();
    this.classList.add('is-letter-open');
    this.classList.remove('is-video-playing');
    if (this.letter) this.letter.hidden = false;

    this.timers.push(window.setTimeout(() => {
      if (this.videoStage) this.videoStage.hidden = true;
      if (this.coverStage) this.coverStage.hidden = true;
      this.classList.add('is-letter-settled');
    }, 500));
  }
}

if (!customElements.get('anniversary-letter-section')) {
  customElements.define('anniversary-letter-section', AnniversaryLetterSection);
}